import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import api from '../../lib/api';
import { useTeamOverview } from '../../hooks/use-team-overview';
import MobileMap from '../../components/maps/MobileMap';
import {
  Loader2,
  Clock,
  Route,
  MapPin,
  Target,
  ChevronDown,
  Store,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';

interface GpsPoint {
  lat: number;
  lng: number;
  timestamp: string;
  speed: number | null;
}

interface StoreVisited {
  store_id: string;
  store_name: string;
  latitude: number;
  longitude: number;
  detected: boolean;
  matched: boolean;
  duration_minutes: number | null;
  avg_distance: number | null;
}

interface HeatmapData {
  points: GpsPoint[];
  stores_visited: StoreVisited[];
}

interface DetectedVisit {
  id: string;
  store_name: string;
  promoter_name: string;
  first_ping_at: string;
  last_ping_at: string;
  duration_minutes: number | null;
  ping_count: number;
  avg_distance_meters: number | null;
  status: 'matched' | 'unmatched' | 'alert_sent';
}

interface DailySummary {
  hours_tracked: number | null;
  km_driven: number | null;
  stores_detected: number | null;
  stores_with_checkin: number | null;
  coverage_rate: number | null;
}

function getTodayStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function formatTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  matched: { label: 'Confirmada', cls: 'bg-green-100 text-green-700' },
  unmatched: { label: 'Sem check-in', cls: 'bg-amber-100 text-amber-700' },
  alert_sent: { label: 'Alerta enviado', cls: 'bg-red-100 text-red-700' },
};

export default function TrackingMap() {
  const [date] = useState(getTodayStr);
  const [promoterId, setPromoterId] = useState<string>('');
  const [showSelector, setShowSelector] = useState(false);

  const { data: teamData, isLoading: teamLoading } = useTeamOverview(60000);

  const promoters = teamData?.promoters ?? [];

  // Find selected promoter name
  const selectedPromoter = promoters.find((p) => p.promoter_id === promoterId);

  // Heatmap / GPS trail
  const { data: heatmapData } = useQuery<HeatmapData>({
    queryKey: ['tracking-heatmap', promoterId, date],
    queryFn: async () => {
      const res = await api.get(`/tracking/heatmap?promoter_id=${promoterId}&date=${date}`);
      return res.data.data;
    },
    enabled: !!promoterId,
  });

  // Detected visits
  const { data: detectedData } = useQuery<{ data: DetectedVisit[] }>({
    queryKey: ['tracking-detected', promoterId, date],
    queryFn: async () => {
      const res = await api.get(`/tracking/detected-visits?promoter_id=${promoterId}&date=${date}`);
      return res.data;
    },
    enabled: !!promoterId,
  });

  // Daily summary
  const { data: summaryData } = useQuery<DailySummary>({
    queryKey: ['tracking-summary', promoterId, date],
    queryFn: async () => {
      const res = await api.get(`/tracking/daily-summary?promoter_id=${promoterId}&date=${date}`);
      const arr = res.data.data;
      if (Array.isArray(arr) && arr.length > 0) return arr[0];
      return { hours_tracked: 0, km_driven: 0, stores_detected: 0, stores_with_checkin: 0, coverage_rate: 0 };
    },
    enabled: !!promoterId,
  });

  const points = heatmapData?.points ?? [];
  const stores = heatmapData?.stores_visited ?? [];
  const detectedVisits = (detectedData?.data ?? []) as DetectedVisit[];
  const summary = summaryData;

  const hoursTracked = Number(summary?.hours_tracked ?? 0);
  const kmDriven = Number(summary?.km_driven ?? 0);
  const storesDetected = Number(summary?.stores_detected ?? 0);
  const coverage = Number(summary?.coverage_rate ?? 0);

  // Map handler — draw polyline + store markers
  const handleMapReady = useCallback(
    (map: L.Map) => {
      // Clear previous layers
      map.eachLayer((layer) => {
        if (layer instanceof L.Polyline || layer instanceof L.Marker || layer instanceof L.CircleMarker) {
          if (!(layer instanceof L.TileLayer)) {
            map.removeLayer(layer);
          }
        }
      });

      const bounds = L.latLngBounds([]);

      // Draw GPS trail polyline
      if (points.length >= 2) {
        const latLngs = points.map((p) => [p.lat, p.lng] as [number, number]);
        L.polyline(latLngs, {
          color: '#3b82f6',
          weight: 3,
          opacity: 0.7,
        }).addTo(map);

        latLngs.forEach((ll) => bounds.extend(ll));
      }

      // Draw store markers
      stores.forEach((store) => {
        if (!store.latitude || !store.longitude) return;

        const color = store.matched ? '#22c55e' : store.detected ? '#f59e0b' : '#9ca3af';
        const icon = L.divIcon({
          className: 'store-tracking-marker',
          html: `<div style="
            width: 28px; height: 28px; border-radius: 50%;
            background: ${color}; border: 3px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex; align-items: center; justify-content: center;
          "><div style="width:8px;height:8px;border-radius:50%;background:white;"></div></div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 14],
          popupAnchor: [0, -18],
        });

        const marker = L.marker([store.latitude, store.longitude], { icon }).addTo(map);
        const statusLabel = store.matched ? 'Check-in realizado' : store.detected ? 'Detectada por GPS' : 'Nao visitada';
        const durationText = store.duration_minutes ? `${store.duration_minutes}min` : '-';

        marker.bindPopup(`
          <div style="min-width: 160px; font-family: system-ui, sans-serif;">
            <div style="font-weight: 700; font-size: 13px; color: #111827; margin-bottom: 4px;">${store.store_name}</div>
            <div style="font-size: 11px; color: #6b7280; margin-bottom: 2px;">${statusLabel}</div>
            <div style="font-size: 11px; color: #6b7280;">Tempo: ${durationText}</div>
          </div>
        `);

        bounds.extend([store.latitude, store.longitude]);
      });

      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [points.length, stores.length, promoterId],
  );

  if (teamLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white px-4 pt-safe pb-3 border-b border-gray-100">
        <h1 className="text-lg font-semibold text-gray-900">Rastreamento GPS</h1>
        <p className="text-xs text-gray-500">Rastro e deteccoes do dia</p>
      </div>

      {/* Promoter selector */}
      <div className="px-4 pt-3">
        <button
          onClick={() => setShowSelector(!showSelector)}
          className="w-full flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3 text-left"
        >
          <span className="text-sm text-gray-700">
            {selectedPromoter ? selectedPromoter.name : 'Selecionar promotor'}
          </span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${showSelector ? 'rotate-180' : ''}`} />
        </button>

        {showSelector && (
          <div className="mt-1 bg-white rounded-xl border border-gray-200 shadow-lg max-h-48 overflow-y-auto z-20 relative">
            {promoters.map((p) => (
              <button
                key={p.promoter_id}
                onClick={() => {
                  setPromoterId(p.promoter_id);
                  setShowSelector(false);
                }}
                className={`w-full text-left px-4 py-2.5 text-sm border-b border-gray-50 last:border-b-0 ${
                  p.promoter_id === promoterId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                }`}
              >
                {p.name}
              </button>
            ))}
            {promoters.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-400 text-center">Nenhum promotor disponivel</div>
            )}
          </div>
        )}
      </div>

      {/* KPI row */}
      {promoterId && (
        <div className="px-4 pt-3">
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white rounded-xl border border-gray-100 p-2.5 text-center">
              <Clock size={14} className="mx-auto text-blue-500 mb-0.5" />
              <p className="text-base font-bold text-gray-900">{hoursTracked.toFixed(1)}h</p>
              <p className="text-[9px] text-gray-500">Tempo</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-2.5 text-center">
              <Route size={14} className="mx-auto text-green-500 mb-0.5" />
              <p className="text-base font-bold text-gray-900">{kmDriven.toFixed(1)}</p>
              <p className="text-[9px] text-gray-500">KM</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-2.5 text-center">
              <MapPin size={14} className="mx-auto text-purple-500 mb-0.5" />
              <p className="text-base font-bold text-gray-900">{storesDetected}</p>
              <p className="text-[9px] text-gray-500">Detectadas</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-2.5 text-center">
              <Target size={14} className="mx-auto text-cyan-500 mb-0.5" />
              <p className="text-base font-bold text-gray-900">{coverage.toFixed(0)}%</p>
              <p className="text-[9px] text-gray-500">Cobertura</p>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      {promoterId && (
        <div className="px-4 pt-3">
          <div className="h-64 rounded-xl overflow-hidden border border-gray-200">
            <MobileMap
              className="h-full w-full"
              onMapReady={handleMapReady}
            />
          </div>
        </div>
      )}

      {/* Detected visits list */}
      {promoterId && (
        <div className="px-4 pt-4 space-y-2">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <Store size={14} />
            Visitas detectadas ({detectedVisits.length})
          </h2>

          {detectedVisits.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
              <MapPin size={28} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Nenhuma deteccao GPS hoje</p>
              <p className="text-xs text-gray-400">As deteccoes aparecem quando o promotor esta proximo a uma loja</p>
            </div>
          ) : (
            detectedVisits.map((v) => {
              const badge = STATUS_BADGE[v.status] ?? STATUS_BADGE.unmatched;
              return (
                <div key={v.id} className="bg-white rounded-xl border border-gray-100 p-3">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{v.store_name}</p>
                      <p className="text-[11px] text-gray-500">
                        {formatTime(v.first_ping_at)} - {formatTime(v.last_ping_at)}
                        {v.duration_minutes != null && ` (${Number(v.duration_minutes)}min)`}
                      </p>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ml-2 ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-gray-400">
                    <span className="flex items-center gap-0.5">
                      {v.status === 'matched' ? (
                        <CheckCircle size={10} className="text-green-500" />
                      ) : (
                        <AlertTriangle size={10} className="text-amber-500" />
                      )}
                      {Number(v.ping_count)} pings
                    </span>
                    {v.avg_distance_meters != null && (
                      <span>{Number(v.avg_distance_meters).toFixed(0)}m distancia media</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* No promoter selected */}
      {!promoterId && (
        <div className="px-4 pt-12 text-center">
          <MapPin size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">Selecione um promotor</p>
          <p className="text-xs text-gray-400 mt-1">para visualizar o rastreamento GPS do dia</p>
        </div>
      )}
    </div>
  );
}
